import { useState, useMemo } from "react";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import PlaceSearch from "./PlaceSearch/PlaceSearch";
import * as redux from "../../redux-store";
import { addCargo } from "../../ducks/app/add-cargo";
import { cargoTypes } from "./types";
import CargoTypesSelector from "./CargoTypesSelector";
import Decimal from "decimal.js-light";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useClients } from "../../ducks/data/clients/hooks";
import StopDateTimeFields from "../../components/StopDateTimeFields";
import { selectHideLoadmeters } from "../../ducks/data/driver-app-settings/selectors";
import { useAppSelector } from "../../redux-store";
import { SearchOption } from "./PlaceSearch/SearchOption";
import {
  selectClientRequiredOnCargos,
  selectDefaultCargoType,
} from "../../ducks/auth/selectors";
import ClientSelector from "./ClientSelector";
import ClientSelectorWithRefresh from "./ClientSelectorWithRefresh";
import Dialog from "@mui/material/Dialog";
import { Button } from "@mui/material";
import { CargoType } from "dora-contracts";
import LoadingButton from "../../components/LoadingButton";
import { useParams } from "../SessionsPage/hooks";
import useFeature from "../../hooks/useFeature";
import { normalizeDecimalInput } from "../../helpers/decimal-input";
import {
  lookupWithRetries,
  PlaceLookupError,
} from "./PlaceSearch/lookup-with-retries";

const AddNewCargoDialog = ({
  onClose,
}: {
  onClose: (result?: { cargoId: string; pickupId: string }) => void;
}) => {
  const dispatch = redux.useAppDispatch();
  const { t } = useTranslation(["translation", "addDropoffPage"]);
  const defaultCargoType = useAppSelector(selectDefaultCargoType);
  const isClientSelectorWithRefreshEnabled = useFeature(
    "mobile-app-client-selector-with-refresh"
  );

  const [form, setForm] = useState({
    cargoType: defaultCargoType || cargoTypes[0],
    clientId: null as string | null,
    // description: "",
  });
  const clients = useClients();
  const now = useMemo(() => DateTime.now(), []);
  const [description, setDescription] = useState("");
  const [invoiceRef, setInvoiceRef] = useState("");
  const [lm, setLm] = useState("13.6");
  const [pickupTime, setPickupTime] = useState(now);
  const [dropoffDate /*, setDropoffDate*/] = useState(now.toJSDate());
  const [pickupLocation, setPickupLocation] = useState<SearchOption | null>(
    null
  );
  const [locationEdited, setLocationEdited] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clientError, setClientError] = useState(false);
  const [placeLookupError, setPlaceLookupError] = useState(false);
  const hideLoadmeters = useAppSelector(selectHideLoadmeters);
  const clientRequired = useAppSelector(selectClientRequiredOnCargos);
  const params = useParams();
  const truckId = (params.truckId as string) || "";
  const routeId = (params.routeId as string) || "";

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPlaceLookupError(false);
    if (!pickupLocation) {
      return;
    }
    if (clientRequired && !form.clientId) {
      setClientError(true);
      return;
    }
    try {
      setLoading(true);
      const result = await dispatch(
        addCargo({
          invoiceRef,
          lm: new Decimal(normalizeDecimalInput(lm)),
          description,
          pickupTime: pickupTime,
          dropoffDate: DateTime.fromJSDate(dropoffDate),
          shouldUsePickupLocationAsDriverLocation: !locationEdited,
          getPickupLocation: {
            getCargoLocation: async () => {
              switch (pickupLocation.type) {
                case "REVERSE_GEOCODE_OPTION":
                case "SEARCH_RESULT":
                  const lookupData = await lookupWithRetries(
                    pickupLocation.value.data
                  );
                  return {
                    googlePlaceId: pickupLocation.value.data.id,
                    placeName: lookupData.placeName || "",
                    country: lookupData.countryCode,
                    position: lookupData.coords,
                    address: lookupData.address,
                    city: lookupData.city,
                    postcode: lookupData.postcode,
                  };
                case "SAVED_LOCATION": {
                  return {
                    placeName: pickupLocation.value.data.place.placeName || "",
                    country: pickupLocation.value.data.place.country,
                    position: pickupLocation.value.data.place.coord,
                    address: pickupLocation.value.data.place.address,
                    city: pickupLocation.value.data.place.city,
                    postcode: pickupLocation.value.data.place.postcode,
                  };
                }
              }
            },
          },
          skipCompleteEvent: true,
          ...form,
          truckId,
          routeId,
        })
      );
      if (result) {
        onClose(result);
      }
    } catch (err) {
      if (!(err instanceof PlaceLookupError)) {
        throw err;
      }
      setPlaceLookupError(true);
    } finally {
      setLoading(false);
    }
  };

  if (!clients) {
    return null;
  }

  const onCargoTypeChange = (cargoType: CargoType) => {
    setForm((f) => ({ ...f, cargoType }));
  };

  return (
    <Dialog open fullScreen onClose={() => onClose()}>
      <Box
        className="px-4 py-2 add-new-cargo"
        sx={{
          paddingTop: "env(safe-area-inset-top, 16px)",
          mt: 3,
        }}
      >
        <Typography variant="h5" component="h1">
          {t("AddNewCargo")}
        </Typography>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <CargoTypesSelector
            value={form.cargoType}
            onValueChange={onCargoTypeChange}
          />
          {isClientSelectorWithRefreshEnabled ? (
            <div className="mt-1">
              <ClientSelectorWithRefresh
                selectedClientId={form.clientId}
                onClientChange={(id) => {
                  setClientError(false);
                  setForm((data) => ({
                    ...data,
                    clientId: id,
                  }));
                }}
              />
            </div>
          ) : (
            <ClientSelector
              selectedClientId={form.clientId}
              onClientChange={(id) => {
                setClientError(false);
                setForm((data) => ({
                  ...data,
                  clientId: id,
                }));
              }}
            />
          )}
          {clientError && (
            <div style={{ color: "red", marginBottom: 8 }}>
              {t("clientRequiredError")}
            </div>
          )}
          <TextField
            label={t("invoiceRefField")}
            margin="dense"
            fullWidth
            value={invoiceRef}
            onChange={(e) => {
              setInvoiceRef(e.target.value);
            }}
          />
          <TextField
            label={t("descriptionField")}
            margin="dense"
            fullWidth
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
          />
          {!hideLoadmeters && (
            <TextField
              label={t("loadmeterField")}
              margin="dense"
              fullWidth
              type="number"
              required
              InputProps={{
                componentsProps: { input: { inputMode: "decimal" } },
              }}
              value={lm}
              onChange={(e) => setLm(e.target.value)}
            />
          )}
          <StopDateTimeFields
            dateFieldLabel={t("pickupDateFieldLabel")}
            timeFieldLabel={t("pickupTimeFieldLabel")}
            value={pickupTime}
            onChange={setPickupTime}
          />
          <PlaceSearch
            label={t("pickupAddressField")}
            location={pickupLocation}
            onLocationChange={setPickupLocation}
            onLocationChangedManually={() => setLocationEdited(true)}
          />
          {placeLookupError && (
            <div style={{ color: "red", marginBottom: 8 }}>
              {t("addDropoffPage:placeLookupError")}
            </div>
          )}
          <LoadingButton
            loading={loading}
            type="submit"
            fullWidth
            size="large"
            variant="contained"
            sx={{ p: 2, mt: 3, mb: 2 }}
          >
            {t("submitButtonText")}
          </LoadingButton>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            onClick={() => onClose()}
          >
            {t("addDropoffPage:cancelButtonLabel")}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default AddNewCargoDialog;
